import FilterDrawer from 'components/Assignments/FilterDrawer';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import useAssignmentChatsMap from 'hooks/chat/useAssignmentChatsMap';
import Desktop from 'pages/StudentAssignments/desktop';
import Mobile from 'pages/StudentAssignments/mobile';
import { PageContext } from 'pages/StudentAssignments/PageProvider';
import Tablet from 'pages/StudentAssignments/tablet';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import assignmentService from 'services/assignment';
import { Loading } from 'shell/components';
import { CHAT_QUERY_OPEN_VALUE, CHAT_QUERY_PARAM, hasUnreadChat, sortSubmissionsByCreatedOn } from 'utils/chat';

const PageLayout = () => {
    const { isLoading, assignments: allAssignments } = useApp();
    const { auth } = useAuth();
    const history = useHistory();
    const { device } = useViewport();
    const ownerAccountId = auth?.id;

    const { filters, setFilters, resetFilters } = useContext(PageContext);
    const studentAssignments = useMemo(() => {
        return (allAssignments || []).filter(
            ({ ai_student_feedback_enabled, is_draft }) => ai_student_feedback_enabled && !is_draft
        );
    }, [allAssignments]);

    const [batch, setBatch] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [submissionsByAssignmentId, setSubmissionsByAssignmentId] = useState(null);
    const assignmentIds = useMemo(() => studentAssignments.map(({ id }) => id), [studentAssignments]);
    const { assignmentChatsByAssignmentId } = useAssignmentChatsMap({
        assignmentIds,
        courseId: auth?.course_id,
        ownerAccountId,
    });

    const getFilterCount = (filters) => {
        let count = 0;
        if (filters.sort !== 'none') count++;
        return count;
    };

    const onClick = ({ id }) => {
        history.push(`/assignments/${id}`);
    };

    const onClickGetFeedback = ({ id }) => {
        history.push(`/assignments/${id}`);
    };

    const onClickViewFeedback = ({ id }) => {
        history.push(`/assignments/${id}`);
    };

    const getLatestSubmissionForAssignment = (assignmentId) => {
        const submissions = submissionsByAssignmentId?.[assignmentId] || [];
        return submissions[submissions.length - 1] || null;
    };

    const onClickInquire = ({ id }) => {
        const latestSubmission = getLatestSubmissionForAssignment(id);
        if (!latestSubmission?.id) return;

        const params = new URLSearchParams({ [CHAT_QUERY_PARAM]: CHAT_QUERY_OPEN_VALUE });
        history.push(`/assignments/${id}/submissions/${latestSubmission.id}?${params.toString()}`);
    };

    const getAssignmentChat = (assignmentId) => {
        return assignmentChatsByAssignmentId?.[assignmentId] || null;
    };

    const hasUnreadChatForAssignment = (assignmentId) => {
        return hasUnreadChat({
            chat: getAssignmentChat(assignmentId),
            accountId: auth?.owner_account_id || ownerAccountId,
        });
    };

    const shouldShowInquireButtonForAssignment = (assignmentId) => {
        return Boolean(getAssignmentChat(assignmentId)?.first_opened_on);
    };

    useEffect(() => {
        if (!allAssignments) return;
        if (!ownerAccountId) return;
        if (!studentAssignments.length) {
            setBatch({});
            setFeedback({});
            setSubmissionsByAssignmentId({});
            return;
        }

        setSubmissionsByAssignmentId(null);

        studentAssignments.forEach(async ({ id }) => {
            const batch = await assignmentService.listAssignmentBatch(
                id,
                JSON.stringify({
                    is_finished: false,
                    owner_account_id: ownerAccountId,
                })
            );
            const feedback = await assignmentService.listAssignmentFeedback(
                id,
                JSON.stringify({ owner_account_id: ownerAccountId })
            );
            const studentSubmissions = await assignmentService.listAssignmentSubmissions(
                id,
                JSON.stringify({
                    type: 'formative',
                    owner_account_id: ownerAccountId,
                })
            );
            setBatch((current) => ({ ...(current || {}), [id]: batch }));
            setFeedback((current) => ({ ...(current || {}), [id]: [...feedback, ...studentSubmissions] }));
            setSubmissionsByAssignmentId((current) => ({
                ...(current || {}),
                [id]: sortSubmissionsByCreatedOn([...studentSubmissions, ...feedback]),
            }));
        });
    }, [allAssignments, ownerAccountId, studentAssignments]);

    const props = {
        batch,
        feedback,
        assignmentChatsByAssignmentId,
        submissionsByAssignmentId,
        filters,
        setFilters,
        getFilterCount,
        onClick,
        onClickGetFeedback,
        onClickInquire,
        onClickViewFeedback,
        hasUnreadChatForAssignment,
        shouldShowInquireButtonForAssignment,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    return isLoading || !feedback || !batch || !assignmentChatsByAssignmentId || !submissionsByAssignmentId ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <FilterDrawer filters={filters} setFilters={setFilters} resetFilters={resetFilters} />
        </>
    );
};

export default PageLayout;
